/* eslint-disable no-undef */
interface TokenPayload {
  sub: string;
  email: string;
  auth_method?: 'microsoft' | 'otp';
  name?: string;
  roles?: string[];
  permissions?: string[];
  exp: number;
  iat: number;
  jti?: string;
  iss?: string;
}

class TokenService {
  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly TOKEN_TYPE_KEY = 'token_type';

  /**
   * Store tokens in sessionStorage
   */
  setTokens(accessToken: string, refreshToken?: string): void {
    sessionStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
    sessionStorage.setItem(this.TOKEN_TYPE_KEY, 'Bearer');
    
    if (refreshToken) {
      sessionStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  /**
   * Store only access token
   */
  setAccessToken(accessToken: string): void {
    sessionStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
    sessionStorage.setItem(this.TOKEN_TYPE_KEY, 'Bearer');
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    return sessionStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    return sessionStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Get token type
   */
  getTokenType(): string {
    return sessionStorage.getItem(this.TOKEN_TYPE_KEY) || 'Bearer';
  }

  /**
   * Get authorization header value
   */
  getAuthorizationHeader(): string | null {
    const token = this.getAccessToken();
    const tokenType = this.getTokenType();
    
    if (token) {
      return `${tokenType} ${token}`;
    }
    
    return null;
  }

  /**
   * Clear all tokens
   */
  clearTokens(): void {
    // Clear from sessionStorage
    sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(this.TOKEN_TYPE_KEY);
    sessionStorage.removeItem('sessionId');
    sessionStorage.removeItem('refreshTimerId');
    
    // Also clear from localStorage if persistent session was enabled
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_TYPE_KEY);
  }

  /**
   * Decode JWT token
   */
  decodeToken(token: string): TokenPayload | null {
    try {
      const parts = token.split('.');
      
      if (parts.length !== 3) {
        return null;
      }
      
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      
      return JSON.parse(decoded) as TokenPayload;
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string): boolean {
    const payload = this.decodeToken(token);
    
    if (!payload || !payload.exp) {
      return true;
    }
    
    // Check if token expires in less than 60 seconds
    const expiryTime = payload.exp * 1000;
    const currentTime = Date.now();
    const bufferTime = 60000; // 60 seconds buffer
    
    return currentTime >= (expiryTime - bufferTime);
  }

  /**
   * Check if current access token is valid
   */
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    
    if (!token) {
      return false;
    }
    
    return !this.isTokenExpired(token);
  }

  /**
   * Get user info from token
   */
  getUserFromToken(): TokenPayload | null {
    const token = this.getAccessToken();
    
    if (!token) {
      return null;
    }
    
    return this.decodeToken(token);
  }

  /**
   * Detect token issuer
   */
  detectTokenIssuer(token: string): 'microsoft' | 'custom' | null {
    const payload = this.decodeToken(token);
    
    if (!payload || !payload.iss) {
      return null;
    }
    
    const issuer = payload.iss;
    
    // Check for Microsoft Azure AD issuer
    if (issuer.includes('login.microsoftonline.com') || 
        issuer.includes('sts.windows.net')) {
      return 'microsoft';
    }
    
    // Check for custom OTP issuer
    if (issuer === 'https://api.horwathhtl.com' || 
        payload.auth_method === 'otp') {
      return 'custom';
    }
    
    return null;
  }

  /**
   * Store token in localStorage for persistent sessions (optional)
   */
  enablePersistentSession(): void {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    
    if (accessToken) {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
    }
    
    if (refreshToken) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  /**
   * Load tokens from localStorage if available
   */
  loadPersistentSession(): boolean {
    const accessToken = localStorage.getItem(this.ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
    
    if (accessToken) {
      // Check if token is still valid
      if (!this.isTokenExpired(accessToken)) {
        sessionStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
        
        if (refreshToken) {
          sessionStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
        }
        
        return true;
      } else {
        // Clear expired tokens from localStorage
        localStorage.removeItem(this.ACCESS_TOKEN_KEY);
        localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      }
    }
    
    return false;
  }

  /**
   * Clear persistent session
   */
  clearPersistentSession(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }
}

// Export singleton instance
export const tokenService = new TokenService();